import {
  withSystem,
  type Type,
  getSystem,
  withMeta,
} from '@packages/systems/wfdl';

import {
  TUnionWithLabels,
  TEmptyRecord,
  TRecord,
} from '@packages/systems/wfdl-ast';
import {TOption} from '@packages/systems/plugins/Option';
import {TColor} from '@packages/systems/plugins/Color';
import {
  TDynamoOption,
  TDynamoPlainText,
  TDynamoEmail,
  TDynamoPhone,
  TDynamoDate,
  TDynamoDateTime,
  TDynamoVideoLink,
  TDynamoLink,
  TDynamoNumber,
  TDynamoImage,
  TDynamoBoolean,
  TDynamoItemRef,
  TDynamoFullSlug,
  TDynamoFileRef,
  TDynamoRichText,
  TDynamoSlug,
  TDynamoPrice,
  TDynamoMultiImage,
  TDynamoItemRefSet,
  TDynamoCurrentItemRef,
  TDynamoRefValue,
} from '@packages/systems/plugins-utils/DynamoGateway';

import {
  type BindingSource,
  type BindingContextType,
  type BindingContextTypeReference,
  toTypeReference,
} from '../../primitives';
import {getBindingSources} from '../binding-sources';
import type {Gateway} from '../../types';

export function getDataType(
  bindingContextType: BindingContextType,
  parentBindingContextType: BindingContextType | null,
  gateways: Gateway[]
): Type {
  const fields = getDataTypeFields({
    typeReference: toTypeReference(bindingContextType),
    gateways,
  });

  let currentItemFields: {_this: Type} | null = null;
  if (bindingContextType.entity === 'Collection') {
    // For CMS collection binding context types, we add a special `_this` field
    // that represents the current item in the binding context.
    currentItemFields = {
      _this: withSystem(
        {label: `Current ${bindingContextType.singularLabel}`},
        TOption(TDynamoCurrentItemRef(TCollectionId(bindingContextType.id)))
      ),
    };
  }

  let dynContextFields: {[dynContextSlug: string]: Type} | null = null;
  if (parentBindingContextType) {
    dynContextFields = getDynContextDataTypeFields(
      parentBindingContextType,
      gateways
    );
  }

  return TRecord({
    ...currentItemFields,
    ...fields,
    ...dynContextFields,
  });
}

function getDataTypeFields({
  typeReference,
  gateways,
  preventExpansion = false,
}: {
  typeReference: BindingContextTypeReference;
  gateways: Gateway[];
  preventExpansion?: boolean;
}): {[slug: string]: Type} {
  const bindingSources = getBindingSources(typeReference, gateways);

  return bindingSources.reduce<{[slug: string]: Type}>(
    (accumulator, bindingSource) => {
      if (bindingSource.path.length === 0) {
        return accumulator;
      }

      if (
        bindingSource.type === 'ItemRef' &&
        bindingSource.meta.preventExpansion !== true &&
        !preventExpansion
      ) {
        const referencedCollectionId =
          bindingSource.meta.legacyReferencedCollectionId;
        if (!referencedCollectionId) {
          return accumulator;
        }

        const itemRefFields = getDataTypeFields({
          typeReference: bindingSource.meta.type,
          gateways,
          preventExpansion: true,
        });

        // @ts-expect-error - TS2532 - Object is possibly 'undefined'.
        const slug = bindingSource.path[bindingSource.path.length - 1].slug;
        const label = bindingSource.label;

        // Add in the full itemRef as a single entry to the schema, wrapped in optional
        accumulator[slug] = withSystem(
          {label},
          TOption(TDynamoItemRef(TCollectionId(referencedCollectionId)))
        );

        // Create a flat structure from fields `friend.age`, `friend.name` and so forth.
        // We know that field slugs do not have dots in them so we can use `.` as a
        // separator.
        return Object.keys(itemRefFields).reduce((acc, curr) => {
          const type = itemRefFields[curr];
          if (!type) {
            return acc;
          }
          const nextField = `${slug}.${curr}`;
          const currLabel = getSystem(type)?.label;
          acc[nextField] = withSystem(
            {
              label: currLabel || curr,
              group: {recordKey: slug},
            },
            type
          );
          return acc;
        }, accumulator);
      }

      let sourceDataType = directTypeMap[bindingSource.type];

      if (bindingSource.type === 'Date') {
        sourceDataType =
          bindingSource.meta.format === 'date-time'
            ? TDynamoDateTime
            : TDynamoDate;
      } else if (
        bindingSource.type === 'Set' &&
        // we only support the Set + ImageRef combination for now
        bindingSource.meta.innerType === 'ImageRef'
      ) {
        sourceDataType = TDynamoMultiImage;
      }

      if (bindingSource.type === 'ItemRefSet') {
        const referencedCollectionId =
          bindingSource.meta.legacyReferencedCollectionId;
        if (referencedCollectionId) {
          sourceDataType = TDynamoItemRefSet(
            TCollectionId(referencedCollectionId)
          );
        }
      }

      if (bindingSource.type === 'Option') {
        const {options} = bindingSource.meta;
        const optionUnion = TUnionWithLabels(
          options.reduce<{[key: string]: {label: string; type: Type}}>(
            (memo, option) => {
              memo[option.id] = {label: option.name, type: TEmptyRecord};
              return memo;
            },
            {}
          )
        );
        sourceDataType = TDynamoOption(optionUnion);
      }

      if (!sourceDataType) {
        return accumulator;
      }

      const slug = bindingSource.path.map((part) => part.slug).join('.');
      const system = {label: bindingSource.label} as const;
      accumulator[slug] = withSystem(system, TOption(sourceDataType));

      return accumulator;
    },
    {}
  );
}

type DirectTypeMap = Partial<Record<BindingSource['type'], Type>>;

const directTypeMap: DirectTypeMap = {
  Number: TDynamoNumber,
  PlainText: TDynamoPlainText,
  Bool: TDynamoBoolean,
  Email: TDynamoEmail,
  Link: TDynamoLink,
  Video: TDynamoVideoLink,
  Color: TColor,
  Phone: TDynamoPhone,
  ImageRef: TDynamoImage,
  FileRef: TDynamoFileRef,
  RichText: TDynamoRichText,
  FullSlug: TDynamoFullSlug,
  Slug: TDynamoSlug,
  CommercePrice: TDynamoPrice,
};

function TCollectionId(collectionId: string): Type {
  return TRecord({
    [collectionId]: TRecord({}),
  });
}

/**
 * Builds a Types record for DYN_CONTEXT values available in a binding context
 *
 * These are used in CMS ItemRef and ItemRefSet field conditions as condition
 * values such as "SomeField of parent Item".
 */
function getDynContextDataTypeFields(
  parentBindingContextType: BindingContextType,
  gateways: Gateway[]
): {[dynContextSlug: string]: Type} {
  const bindingSources = getBindingSources(
    toTypeReference(parentBindingContextType),
    gateways
  );

  const parentLabel = parentBindingContextType.singularLabel;

  const baseDynContextFields: {[slug: string]: Type} = {};
  if (parentBindingContextType.entity === 'Collection') {
    baseDynContextFields.DYN_CONTEXT = withSystem(
      {label: `Parent ${parentLabel}`},
      withMeta(
        {parentName: parentLabel},
        TOption(TDynamoRefValue(TCollectionId(parentBindingContextType.id)))
      )
    );
  }

  return bindingSources.reduce<{[dynContextSlug: string]: Type}>(
    (accumulator, bindingSource) => {
      if (bindingSource.path.length !== 1) {
        return accumulator;
      }

      if (
        bindingSource.type === 'ItemRef' ||
        bindingSource.type === 'ItemRefSet'
      ) {
        const referencedCollectionId =
          bindingSource.meta.legacyReferencedCollectionId;
        if (referencedCollectionId) {
          const slug = bindingSource.path.map((part) => part.slug).join('.');
          const dynContextSlug = `DYN_CONTEXT.${slug}`;

          const label = `${bindingSource.label} of parent ${parentLabel}`;

          accumulator[dynContextSlug] = withSystem(
            {label},
            withMeta(
              {fieldName: bindingSource.label, parentName: parentLabel},
              TOption(TDynamoRefValue(TCollectionId(referencedCollectionId)))
            )
          );
        }
      }

      return accumulator;
    },
    baseDynContextFields
  );
}
